import { useCallback } from 'react'

import { usePatchCopyDoneMutation } from '@/features/yakureki/hooks/queries/useSoapMutations'
import { useToast } from '@/shared/contexts/ToastContext'
import { copyToClipboard } from '@/shared/utils/clipboard'

// PostRecordingView のコピー導線を 2 系統に整理する.
//
//  系統1 useCopyAndComplete: clipboard + 完了 (mark-copied).
//    → SOAP 成果物そのもの (全文 master / 各 section S/O/A/EP/CP/OP) のコピー用.
//      コピー = 成果物を取り出した = 完了、 として患者一覧の完了側へ移す. mark-copied は
//      冪等なので section を複数回コピーしても完了状態は崩れない.
//
//  系統2 useCopyText: clipboard のみ (完了させない).
//    → 成果物でない情報のコピー用. FP (フォローアップ計画 = 来局を待たない継続フォロー、
//      SOAP とは別軸) / 発話 (会話の生記録) / ハイライト (会話の抽出). これらは「成果物を
//      納品した」 わけではないので完了に持っていかない.
//
// どちらも空文字なら no-op、 コピーは copyToClipboard (shared/utils/clipboard) の 1 本に
// 寄せ、 失敗したら error toast を出す (= 「押したのに何も起きない」を作らない).

// 系統1: clipboard + 完了.
export const useCopyAndComplete = (eventId: string) => {
  const { showToast } = useToast()
  const patchCopyDoneMutation = usePatchCopyDoneMutation()

  return useCallback(
    async (text: string) => {
      if (!text) return
      if (!(await copyToClipboard(text))) {
        showToast('コピーに失敗しました', 'error')
        return
      }
      showToast('コピー・完了へ移動しました', 'info', 2000)
      // 完了マーク (mark-copied) の失敗はコピーの失敗ではない。文言を分けて、
      // 「コピーはできたが完了にならなかった」を取り違えさせない.
      try {
        await patchCopyDoneMutation.mutateAsync(eventId)
      } catch {
        showToast('完了への移動に失敗しました', 'error')
      }
    },
    [eventId, showToast, patchCopyDoneMutation]
  )
}

// 系統2: clipboard のみ. successMessage で「何をコピーしたか」を呼び出し側が指定する.
export const useCopyText = () => {
  const { showToast } = useToast()

  return useCallback(
    async (text: string, successMessage: string) => {
      if (!text) return
      if (await copyToClipboard(text)) {
        showToast(successMessage, 'success')
      } else {
        showToast('コピーに失敗しました', 'error')
      }
    },
    [showToast]
  )
}
